import { autoinject } from 'aurelia-dependency-injection';
import { MdcSnackbarService } from '@aurelia-mdc-web/snackbar';
import { ClientNotification, EntityChanged, Notification, NotificationsClient, NotificationStep, NotificationStepStatus } from '@dynamik/common/clients/notification-clients';
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { NotificationDB } from './notification-db';
import { isAfter } from '../../date-fns-local';
import { CustomerService } from '@dynamik/common';
import { combineLatestWith, concatMap, from, map, switchMap, filter, Observable, Subject, takeUntil } from '../../rxjs-local';
import Dexie from 'dexie';
import { jwtAuthenticator } from "nats.ws";
import { wsconnect, NatsConnection } from "@nats-io/nats-core";
import { jetstreamManager, AckPolicy, DeliverPolicy, ReplayPolicy, Consumer } from "@nats-io/jetstream";
import { defer, finalize } from 'rxjs';
import { getNatsUrl } from '@dynamik/common';
import { UserContext } from '../user-context';
import { Constructor } from '../../interfaces/constructor';
import { EntityChangedTyped } from '../entity-changed-typed';

export const NOTIFICATIONS_UPDATED_EVENT = 'notifications:updated';

@autoinject
export class NotificationService {
  constructor(private snackbarService: MdcSnackbarService, private db: NotificationDB, private customerService: CustomerService,
    private notificationsClient: NotificationsClient, private userContext: UserContext) { }

  userChanged$ = new Subject<void>();
  entityChanged$ = new Subject<EntityChanged>();
  clientNotification$ = new Subject<ClientNotification>();
  notifications$ = new BehaviorSubject<Notification[]>([]);
  refresh$ = new BehaviorSubject<void>(undefined);
  completeNotification$ = new Subject<Notification>();
  natsConnection: NatsConnection;
  consumer: Consumer;


  getEntityChangedSubscription<T extends { init(data: any): void }>(type: Constructor<T>, destroy$?: Subject<void>): Observable<EntityChangedTyped<T>> {
    let sub = this.entityChanged$.pipe(filter(x => x.type === type.name));
    sub = destroy$ ? sub.pipe(takeUntil(destroy$)) : sub;
    return sub.pipe(map(x => {
      const ect = new EntityChangedTyped<T>();
      ect.companyIdentityId = x.companyIdentityId;
      ect.type = x.type;
      ect.eventType = x.eventType;
      ect.data = new type();
      ect.data.init(x.data);
      ect.notifyStepId = x.notifyStepId;
      ect.notifyStatus = x.notifyStatus;
      ect.notifyMainMessage = x.notifyMainMessage;
      ect.notifyStepMessage = x.notifyStepMessage;
      ect.notifyTimeStamp = x.notifyTimeStamp;
      ect.activityId = x.activityId;
      ect.notifyType = x.notifyType;
      ect.notifyStepType = x.notifyStepType;
      ect.notifyStepProgress = x.notifyStepProgress;
      return ect;
    }));
  }


  selectedCustomerSub$ = this.customerService.selectedCustomer$
    .pipe(
      combineLatestWith(this.refresh$),
      switchMap(x => this.getRecentNotifications())
    )
    .subscribe(this.notifications$);

  clientNotificationSub$ = this.clientNotification$
    .pipe(
      concatMap(x => this.handleNotification(x))
    )
    .subscribe(async x => {
      this.refresh$.next();
      if (x) {
        this.completeNotification$.next(x);
      }
    });

  private async handleNotification(cn: ClientNotification) {
    if (cn.natsSequence !== undefined && await this.db.processedNatsSequences.get(cn.natsSequence)) {
      return;
    }

    if (cn.notification) {
      const existingSteps = (await this.db.notifications.get(cn.notification.id))?.steps || [];
      // merge existing steps with new steps, keeping the most recent one for each step
      cn.notification.steps = [...existingSteps, ...cn.notification.steps]
        .groupBy(s => s.step).toArray()
        .map(x => x.value.sort((a, b) => isAfter(b.date, a.date) ? 1 : -1)[0])
        .sort((a, b) => a.step - b.step);
      await this.db.notifications.put(cn.notification);
    }

    if (cn.step) {
      let n = await this.db.notifications.get(cn.step.notificationId);
      if (!n) {
        n = new Notification({ id: cn.step.notificationId, steps: [] });
      }
      const existingStepIndex = n.steps.findIndex(s => s.step === cn.step.step);
      if (existingStepIndex > -1) {
        // replace existing step if incoming is newer
        if (isAfter(cn.step.date, n.steps[existingStepIndex].date)) {
          cn.step.stepName ??= n.steps[existingStepIndex].stepName; // keep old step name if new one is missing it
          // keep old progress
          (cn.step.progresses ??= []).push(...n.steps[existingStepIndex].progresses);
          n.steps[existingStepIndex] = cn.step;
        }
      } else {
        n.steps.push(cn.step);
      }
      n.steps.sort((a, b) => a.step - b.step);
      await this.db.notifications.put(n);
      cn.notification = n;
    }

    if (cn.progress) {
      let n = await this.db.notifications.get(cn.progress.notificationId);
      if (!n) {
        n = new Notification({ id: cn.progress.notificationId, steps: [] });
      }
      let s = n.steps.find(s => s.step === cn.progress.step);
      if (!s) {
        s = new NotificationStep({ step: cn.progress.step, progresses: [] });
        n.steps.push(s);
      }
      (s.progresses ??= []).push(cn.progress);
      // Nothing on the backend closes a progress-driven step; it is done once every expected tick has arrived.
      if (s.status === NotificationStepStatus.InProgress && s.expectedProgress && s.progresses.length >= s.expectedProgress) {
        s.status = NotificationStepStatus.Success;
        s.date = cn.progress.date;
      }
      n.steps.sort((a, b) => a.step - b.step);
      await this.db.notifications.put(n);
      cn.notification = n;
    }

    if (cn.natsSequence !== undefined) {
      await this.db.processedNatsSequences.put({ companyIdentityId: cn.companyIdentityId, sequence: cn.natsSequence });
    }

    return cn.notification;
  }

  async getRecentNotifications(): Promise<Notification[]> {
    const sixtyMinAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
    return (await this.db.notifications
      .where('[companyIdentityId+date]')
      .between([this.customerService.selectedCustomer.companyId, sixtyMinAgo], [this.customerService.selectedCustomer.companyId, Dexie.maxKey])
      .reverse()
      .toArray()).filter(x => !!x.text && !x.deleted);
  }

  async getNotifications(includeDeleted: boolean): Promise<Notification[]> {
    return (await this.db.notifications
      .where('[companyIdentityId+date]')
      .between([this.customerService.selectedCustomer.companyId, Dexie.minKey], [this.customerService.selectedCustomer.companyId, Dexie.maxKey])
      .reverse()
      .toArray()).filter(x => !!x.text && (includeDeleted || !x.deleted));
  }

  async deleteNotifications(n: Notification[]) {
    n.forEach(notification => notification.deleted = true);
    await this.db.notifications.bulkPut(n);
    this.refresh$.next();
  }

  async notifySuccess(message: string) {
    return this.snackbarService.open(message, null, {
      classes: 'custom-snackbar--success-fill-color custom-snackbar--viewport-margin',
      dismissible: true
    });
  }

  async notifyError(message: string) {
    return this.snackbarService.open(message, null, {
      classes: 'custom-snackbar--error-fill-color custom-snackbar--viewport-margin',
      dismissible: true
    });
  }

  async start() {
    try {
      // const token = await this.notificationsClient.getToken(this.customerService.selectedCustomer.companyId);
      // console.log(token.env === 'production' ? 'wss://insight.dynamik.no/leaf' : `wss://${token.env.replace('-dev', '')}.env.insight.dynamik.no/leaf`)
      // this.natsConnection = await wsconnect({
      //   servers: token.env === 'production' ? 'wss://insight.dynamik.no/leaf' : `wss://${token.env.replace('-dev', '')}.env.insight.dynamik.no/leaf`,
      //   authenticator: jwtAuthenticator(token.jwt, new TextEncoder().encode(token.seed)),
      //   inboxPrefix: `_INBOX.${token.sessionId}`,
      // });
      const user = await this.userContext.get();

      this.customerService.selectedCustomer$
        .pipe(
          switchMap(async x => {
            // 1. Teardown old connection if it exists
            if (this.natsConnection) {
              await this.natsConnection.close().catch(() => { });
            }
            // 2. Get a FRESH token for the NEW tenant
            const token = await this.notificationsClient.getToken(x.companyId);

            // 3. Establish a NEW connection with the new permissions
            const natsUrl = getNatsUrl(token.env);
            console.log(natsUrl);
            const nc = await wsconnect({
              servers: natsUrl,
              authenticator: jwtAuthenticator(token.jwt, new TextEncoder().encode(token.seed)),
              inboxPrefix: `_INBOX.${token.sessionId}`,
            });

            this.natsConnection = nc;

            const streamName = `${token.env}:NOTIFICATIONS`;
            console.log(`${token.env}:NOTIFICATIONS`)
            const subjJS = `${token.env}.notifications.company.${x.companyId}`;
            void this.subscribeEntityChanged(nc, `${token.env}.entitychanged.company.${x.companyId}`);
            void this.subscribeUserChanged(nc, `${token.env}.users.${user.email?.toLowerCase()}`);

            const jsm = await jetstreamManager(this.natsConnection, { domain: "hub" });
            const js = jsm.jetstream()

            const processedSequence = await this.db.processedNatsSequences
              .where('[companyIdentityId+sequence]')
              .between([x.companyId, Dexie.minKey], [x.companyId, Dexie.maxKey])
              .reverse()
              .first();

            // await this.consumer?.delete();
            const consumerInfo = await jsm.consumers.add(streamName, {
              // durable_name: `App`, //dont need to use subject but we just need to actualy set the name
              name: `App_${token.sessionId}`, // this needs to be set to create consumer create request with subject on the create command
              filter_subject: subjJS, //thisis the actual subject filter
              ack_policy: AckPolicy.Explicit,
              inactive_threshold: 20_000_000_000,
              deliver_policy: processedSequence?.sequence ? DeliverPolicy.StartSequence : DeliverPolicy.StartTime,
              opt_start_seq: processedSequence?.sequence + 1,
              replay_policy: ReplayPolicy.Instant,
              opt_start_time: processedSequence?.sequence ? undefined : new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            });
            this.consumer = js.consumers.getConsumerFromInfo(consumerInfo);
            return this.consumer;
          }),
          switchMap(consumer =>
            defer(() => consumer.consume()).pipe(
              switchMap(messages =>
                from(messages).pipe(
                  finalize(() => {
                    try {
                      messages.close();
                    } catch { }
                  })
                )
              )
            )
          ),
          map(m => {
            m.ack();
            const cn = ClientNotification.fromJS(m.json());
            cn.natsSequence = m.seq;
            return cn;
          })
        )
        .subscribe(this.clientNotification$);
    } catch (e) {
      console.error('Error connecting to NATS', e);
    }
  }

  private async subscribeEntityChanged(natsConnection: NatsConnection, subject: string) {
    try {
      for await (const message of natsConnection.subscribe(subject)) {
        this.entityChanged$.next(EntityChanged.fromJS(message.json()));
      }
    } catch (e) {
      console.error('Error receiving entity changed events from NATS', e);
    }
  }

  private async subscribeUserChanged(natsConnection: NatsConnection, subject: string) {
    try {
      for await (const message of natsConnection.subscribe(subject)) {
        this.userChanged$.next();
      }
    } catch (e) {
      console.error('Error receiving user changed events from NATS', e);
    }
  }
}
